import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthPage from "@/pages/AuthPage";
import LandlordDashboard from "@/pages/LandlordDashboard";
import PropertiesPage from "@/pages/PropertiesPage";
import TenantsPage from "@/pages/TenantsPage";
import RentsPage from "@/pages/RentsPage";
import MaintenancePage from "@/pages/MaintenancePage";
import IncidentsPage from "@/pages/IncidentsPage";
import DocumentsPage from "@/pages/DocumentsPage";
import OverduePage from "@/pages/OverduePage";
import SettingsPage from "@/pages/SettingsPage";
import FinancialsPage from "@/pages/FinancialsPage";
import PnlPage from "@/pages/PnlPage";
import NotesPage from "@/pages/NotesPage";
import LettersPage from "@/pages/LettersPage";
import TeamPage from "@/pages/TeamPage";
import SecurityLogPage from "@/pages/SecurityLogPage";
import TenantDashboard from "@/pages/TenantDashboard";
import AiAssistantPage from "@/pages/AiAssistantPage";
import "@/App.css";

function Loading() {
  return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Loading…</div>;
}

const STAFF_ROLES = ["super_admin", "landlord", "employee"];
const ADMIN_ROLES = ["super_admin", "landlord"];

function Guarded({ children, role, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/auth" replace />;
  const allowed = roles ? roles.includes(user.role) : (role ? user.role === role : true);
  if (!allowed) return <Navigate to={STAFF_ROLES.includes(user.role) ? "/landlord" : "/tenant"} replace />;
  return children;
}



function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/auth" replace />;
  return <Navigate to={STAFF_ROLES.includes(user.role) ? "/landlord" : "/tenant"} replace />;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  return (
    <Routes>
      <Route path="/auth" element={user ? <Navigate to={STAFF_ROLES.includes(user.role) ? "/landlord" : "/tenant"} replace /> : <AuthPage />} />
      <Route path="/" element={<RootRedirect />} />

      {/* Staff workspace — accessible to super_admin / landlord / employee */}
      <Route path="/landlord" element={<Guarded roles={STAFF_ROLES}><LandlordDashboard /></Guarded>} />
      <Route path="/landlord/properties" element={<Guarded roles={STAFF_ROLES}><PropertiesPage /></Guarded>} />
      <Route path="/landlord/tenants" element={<Guarded roles={STAFF_ROLES}><TenantsPage /></Guarded>} />
      <Route path="/landlord/rents" element={<Guarded roles={STAFF_ROLES}><RentsPage /></Guarded>} />
      <Route path="/landlord/overdue" element={<Guarded roles={STAFF_ROLES}><OverduePage /></Guarded>} />
      <Route path="/landlord/maintenance" element={<Guarded roles={STAFF_ROLES}><MaintenancePage /></Guarded>} />
      <Route path="/landlord/incidents" element={<Guarded roles={STAFF_ROLES}><IncidentsPage /></Guarded>} />
      <Route path="/landlord/documents" element={<Guarded roles={STAFF_ROLES}><DocumentsPage /></Guarded>} />
      <Route path="/landlord/financials" element={<Guarded roles={STAFF_ROLES}><FinancialsPage /></Guarded>} />
      <Route path="/landlord/pnl" element={<Guarded roles={STAFF_ROLES}><PnlPage /></Guarded>} />
      <Route path="/landlord/notes" element={<Guarded roles={STAFF_ROLES}><NotesPage /></Guarded>} />
      <Route path="/landlord/letters" element={<Guarded roles={STAFF_ROLES}><LettersPage /></Guarded>} />
      {/* Admin-only routes */}
      <Route path="/landlord/team" element={<Guarded roles={ADMIN_ROLES}><TeamPage /></Guarded>} />
      <Route path="/landlord/security" element={<Guarded roles={ADMIN_ROLES}><SecurityLogPage /></Guarded>} />
      <Route path="/landlord/ai" element={<Guarded roles={ADMIN_ROLES}><AiAssistantPage /></Guarded>} />
      <Route path="/landlord/settings" element={<Guarded roles={ADMIN_ROLES}><SettingsPage /></Guarded>} />

      {/* Tenant */}
      <Route path="/tenant" element={<Guarded role="tenant"><TenantDashboard /></Guarded>} />
      <Route path="/tenant/maintenance" element={<Guarded role="tenant"><MaintenancePage /></Guarded>} />
      <Route path="/tenant/incidents" element={<Guarded role="tenant"><IncidentsPage /></Guarded>} />
      <Route path="/tenant/rents" element={<Guarded role="tenant"><RentsPage readOnly /></Guarded>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="bottom-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
